import { defineStore } from 'pinia';
import { ref } from 'vue';
import quotationsAPI from '@/api/quotations';
import type { Quotation } from '@/types/Quotation';
import { useAlertStore } from '@/store/alertStore';
import { useLoaderStore } from '@/store/loader';

export const useQuotationsStore = defineStore('quotations', () => {
  const quotations = ref<Quotation[]>([]);
  const quoted_by_user = ref<any>([]);
  const quotations_totals = ref<any>([]);

  const alertStore = useAlertStore();
  
  const fetchQuotations = async (params: any): Promise<{ data: Quotation[]; total: number }> => {
    try {
      const response = await quotationsAPI.getQuotations(params);
      const data: Quotation[] = response.data.data;
      const total = response.data.meta.total;
      quotations.value = data;
      return { data, total };
    } catch (error) {
      console.error('Error fetching quotation s:', error);
      throw error;
    }
  };

  const fetchQuotationById = async (id: number) => {
    try {
      const response = await quotationsAPI.getQuotationById(id);
      return response.data.data as Quotation;
    } catch (error) {
      console.error(`Error fetching quotation  with ID ${id}:`, error);
      throw error;
    }
  };

  const createQuotation = async (quotationData: Partial<Quotation>) => {
    try {
      const response = await quotationsAPI.createQuotation(quotationData);
      if (!response.data.success) {
        throw response.data;
      }
      quotations.value.push(response.data.data as Quotation);
      return response.data;
    } catch (error) {
      console.error('Error creating quotation :', error);
      throw error;
    }
  };

  const updateQuotation = async (id: number, quotationData: Partial<Quotation>) => {
    try {
      const response = await quotationsAPI.updateQuotation(id, quotationData);
      if (!response.data.success) {
        throw response.data;
      }
      const updated = response.data.data as Quotation;
      const index = quotations.value.findIndex((c:any) => c.id === id);
      if (index !== -1) {
        quotations.value[index] = updated;
      }
      return response.data;
    } catch (error) {
      console.error(`Error updating quotation  with ID ${id}:`, error);
      throw error;
    }
  };

  const deleteQuotation = async (id: number) => {
    try {
      await quotationsAPI.deleteQuotation(id);
      quotations.value = quotations.value.filter((c) => c.id !== id);
    } catch (error) {
      console.error(`Error deleting quotation  with ID ${id}:`, error);
      throw error;
    }
  };


  const quotedByUser = async (params: any): Promise<{ data: any; }> => {
    try{
      const response = await quotationsAPI.getQuotedByUser(params);
      const data = response.data;
      quoted_by_user.value = data
      return data;
    } catch (error){
      throw error;
    }
  };

  const quotationsTotals = async (params: any): Promise<{ data: any; }> => {
    try{
      const response = await quotationsAPI.getQuotationsTotals(params);
      const data = response.data;
      quotations_totals.value = data
      return data;
    } catch (error){
      throw error;
    }
  };

  const loader = useLoaderStore();
  
  const exportPdfAndDownload = async (id: number) => {
    try {
      loader.show('Generando PDF...');
      const response = await quotationsAPI.exportPdf(id);
      /*if (!response.data.success) {
        throw response.data;
      }*/
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Cotizacion #${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error downloading PDF for quotation ID ${id}:`, error);
      throw error;
    }finally {
      loader.hide();
    }
  };

  const convertQuotation = async (id: number, client: any, additional_data:any) => {
    try {
      const response = await quotationsAPI.convertQuotation(id, client, additional_data);
      if (!response.data.success) {
        throw response.data;
      }
      /*const updated = response.data.data as Quotation;
      const index = quotations.value.findIndex((c:any) => c.id === id);
      if (index !== -1) {
        quotations.value[index] = updated;
      }*/
      alertStore.showAlert(response.data.message || 'Cotización convertida con éxito', 'success');
      return response.data;
    } catch (error:any) {
      alertStore.showAlert(
        error?.response?.data?.message || 'Ocurrió un error al convertir la cotización.',
        'error'
      );
      throw error;
    }
  };

  const authorizeQuotationItem = async (id: any) => {
    try{
      const response = await quotationsAPI.authorizeQuotationItem(id);
      if (!response.data.success) {
        throw response.data;
      }
      alertStore.showAlert('Detalle de cotización autorizada con éxito', 'success');
      return response;
    } catch (error:any){
      alertStore.showAlert(
        error?.response?.data?.message || 'Ocurrió un error al autorizar el detalle de cotización.',
        'error'
      );
      throw error;
    }
  };
  
  const authorizeQuotation = async (quotationData: any) => {
    try {
      const response = await quotationsAPI.authorizeQuotation(quotationData);
      if (!response.data.success) {
        throw response.data;
      }
      const updated = response.data.data as Quotation;
      const index = quotations.value.findIndex((c:any) => c.id === quotationData.quotation_id);
      if (index !== -1) {
        quotations.value[index] = updated;
      }
      alertStore.showAlert('Cotización autorizada con éxito', 'success');
      return response.data;
    } catch (error:any) {
      alertStore.showAlert(
        error?.response?.data?.message || 'Ocurrió un error al autorizar la cotización.',
        'error'
      );
      throw error;
    }
  };

  const exportCSV = async (params: string, body: any) => {
    try {
      return await quotationsAPI.exportCSV(params, body);
    } catch (error) {
      console.error('Error exportando archivo:', error);
      throw error;
    }
  };

  return {
    quotations,
    fetchQuotations,
    fetchQuotationById,
    createQuotation,
    updateQuotation,
    deleteQuotation,
    quotedByUser,
    quotationsTotals,
    quoted_by_user,
    quotations_totals,
    exportPdfAndDownload,
    convertQuotation,
    authorizeQuotationItem,
    authorizeQuotation,
    exportCSV
  };
});
